/*
  Express checkout with a strap (Hype Locker UK, added 17 Oct 2026). Works with the Easify strap box on the watch page.

  The problem this fixes
    With a strap picked in the Easify box, a tap on "Buy with Apple Pay", "Buy with Shop" or another express button never
    reached Shopify's button: Easify cancelled the tap, added the watch and the strap to the cart and sent the customer to
    the normal checkout page.

  What it does (two small jobs)
    1. Strap picked: it keeps hidden fields in the product form that Shopify's express buttons read at the moment of the tap
       (Shopify's own method for buying extra products with express checkout:
       https://shopify.dev/docs/storefronts/themes/pricing-payments/accelerated-checkout/multiple-product-checkout ):
       shopify-additional-items[1][id] = the strap's variant, [quantity] = the number of watches, and one numbered set per
       extra strap. Apple Pay, Google Pay and Shop Pay then buy the watch and the strap(s) together and show the total,
       including the BUY2TAKE20 discount, themselves. No strap picked: no fields, the form stays exactly as it was.
    2. Strap picked and the tap is on an express button: while the tap travels down the page it is told it landed on the page
       itself (the click's "target" is swapped for the html element) and the real target is put back before the tap reaches
       Shopify's button. Easify checks that target, finds it is none of its buttons and steps aside, while Shopify's button gets
       the real tap, so the Apple Pay sheet opens just as it does for the watch on its own.

  What it does NOT do
    It never clicks, edits or removes anything Easify owns, and leaves Add to cart, the cart drawer, prices and the strap box
    alone. Shopify's cart ignores the hidden fields, so Add to cart cannot add a strap twice.

  If something is off
    Every step is wrapped so an error here cannot block a tap. If the browser cannot swap the target, or Easify changes how it
    listens, the page simply behaves as before: the express buttons take the customer to the normal checkout page.
    It must be loaded before Easify's own script (it is: Easify's script sits at the end of the page).
    Switch off for a test: window.hlExpressStrap.enabled = false. Roll back: delete the hl-express-strap line from
    layout/theme.liquid (the file can stay).
*/
(function () {
  'use strict';
  if (window.hlExpressStrap) return;

  var WRAPPER = '[data-shopify="payment-button"]'; // Shopify's wrapper around the express buttons; the buttons read the form around it
  var FIELD = 'shopify-additional-items';          // Shopify's name for extra products in an express checkout
  var HOLDER = 'data-hl-express-items';            // marks the little hidden box that holds our fields

  var api = window.hlExpressStrap = {
    enabled: true,
    items: function () { return currentIds(); } // the strap variants that would go to an express checkout right now
  };

  /* The swap needs the browser to allow an own "target" on a click and to take it away again; if not, do nothing at all. */
  function canSwapTarget() {
    try {
      var probe = new Event('hl-probe');
      Object.defineProperty(probe, 'target', { value: document, configurable: true });
      var worked = probe.target === document;
      delete probe.target;
      return worked && probe.target === null;
    } catch (e) {
      return false;
    }
  }
  if (typeof WeakSet !== 'function' || typeof MutationObserver !== 'function' || !canSwapTarget()) {
    api.enabled = false;
    return;
  }

  function label(node) {
    return node ? node.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  /* The strap variant that Easify has behind a button, from Easify's own page data. */
  function variantFor(setId, name) {
    var sets = (window.TPOConfigs && window.TPOConfigs.option_sets_searchable) || [];
    for (var i = 0; i < sets.length; i++) {
      var data = sets[i] && sets[i].data;
      if (!data || String(data.option_set_id) !== String(setId)) continue;
      var options = data.options || [];
      for (var j = 0; j < options.length; j++) {
        var values = options[j].option_types || [];
        for (var k = 0; k < values.length; k++) {
          if (String(values[k].label).trim() === name && values[k].variant_id) return String(values[k].variant_id);
        }
      }
    }
    return '';
  }

  /* The straps that are picked in the Easify box(es) right now (variant ids, in page order, no repeats). */
  function currentIds() {
    var ids = [];
    try {
      var roots = document.querySelectorAll('.po-optionSet-root[data-option-set-id]');
      for (var r = 0; r < roots.length; r++) {
        var setId = roots[r].getAttribute('data-option-set-id');
        var groups = roots[r].querySelectorAll('.po-buttons-optionGroup');
        for (var g = 0; g < groups.length; g++) {
          var tiles = groups[g].children;
          for (var t = 0; t < tiles.length; t++) {
            if (!tiles[t].querySelector('.po-buttonOption-selected')) continue;
            var id = variantFor(setId, label(tiles[t].querySelector('.po-buttonOption-label')));
            if (id && ids.indexOf(id) === -1) ids.push(id);
          }
        }
      }
    } catch (e) { /* Easify changed its page: treat as nothing picked */ }
    return ids;
  }

  /* The product form that the express buttons read (the one around their wrapper). */
  function paymentForm() {
    var wrapper = document.querySelector(WRAPPER);
    return wrapper && wrapper.closest ? wrapper.closest('form') : null;
  }

  /* Number of watches, read the way Easify reads it (it gives each strap the same quantity). */
  function watchQuantity(form) {
    var field = form.elements && form.elements.quantity;
    if (field && !field.tagName && field.length) field = field[0];
    var n = Math.floor(Number(field && field.value));
    return n > 0 ? n : 1;
  }

  function hidden(name, value) {
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    return input;
  }

  /* Make the hidden fields match the picked straps. Writes only when something changed; none picked removes them all. */
  function render(form, ids) {
    var quantity = watchQuantity(form);
    var signature = ids.map(function (id) { return id + 'x' + quantity; }).join(',');
    var holder = form.querySelector('[' + HOLDER + ']');
    if (!ids.length) {
      if (holder) holder.parentNode.removeChild(holder);
      return;
    }
    if (holder && holder.getAttribute(HOLDER) === signature) return;
    if (!holder) {
      holder = document.createElement('span');
      holder.style.display = 'none';
      form.appendChild(holder);
    }
    holder.setAttribute(HOLDER, signature);
    holder.textContent = '';
    ids.forEach(function (id, i) {
      holder.appendChild(hidden(FIELD + '[' + (i + 1) + '][id]', id));
      holder.appendChild(hidden(FIELD + '[' + (i + 1) + '][quantity]', String(quantity)));
    });
  }

  function update() {
    try {
      var form = paymentForm();
      if (form) render(form, api.enabled ? currentIds() : []);
    } catch (e) { /* never break the page */ }
  }

  /* ---- The tap. Registered right now, before Easify (whose script is at the end of the page) registers its own. ---- */
  var swapped = new WeakSet(); // the clicks whose target we swapped, so only those get put back

  window.addEventListener('click', function (event) {
    try {
      if (!api.enabled) return;
      var target = event.target;
      if (!target || !target.closest || !target.closest(WRAPPER)) return; // not a tap on the express buttons
      var form = paymentForm();
      if (!form) return;
      var ids = currentIds();
      render(form, ids); // last look at the picked straps and the quantity before Shopify's button reads the form
      if (!ids.length) return; // no strap: leave the tap to Easify and Shopify exactly as before
      Object.defineProperty(event, 'target', { value: document.documentElement, configurable: true });
      swapped.add(event);
    } catch (e) { /* never get in the way of a tap */ }
  }, true);

  /* Next stop for the tap after every listener on the window: put the real target back before it travels on to the buttons. */
  document.addEventListener('click', function (event) {
    try {
      if (swapped.has(event)) {
        delete event.target;
        swapped.delete(event);
      }
    } catch (e) { /* nothing to put back */ }
  }, true);

  /* ---- Keeping the fields up to date while the customer picks straps ---- */
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    Promise.resolve().then(function () {
      queued = false;
      update();
    });
  }

  var watched = new WeakSet();
  function watchBoxes() {
    var roots = document.querySelectorAll('.po-optionSet-root[data-option-set-id]');
    for (var i = 0; i < roots.length; i++) {
      if (watched.has(roots[i])) continue;
      watched.add(roots[i]);
      new MutationObserver(schedule).observe(roots[i], { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }
  }

  function start() {
    watchBoxes();
    update();
    // Easify builds (and may rebuild) its box after the page has loaded, so keep looking for it
    new MutationObserver(function () { watchBoxes(); schedule(); }).observe(document.documentElement, { childList: true, subtree: true });
    var quantityChanged = function (event) {
      if (event.target && event.target.name === 'quantity') schedule();
    };
    document.addEventListener('change', quantityChanged, true);
    document.addEventListener('input', quantityChanged, true);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
